import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Download, CheckCircle, XCircle, Clock, User, Play } from 'lucide-react';
import { useApi } from '../hooks/useUser.jsx';

/**
 * Results Page
 * 
 * Display test results after authentication - styled to match main IDNTY website
 */
export function Results() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { fetchWithUser } = useApi();
  
  const [session, setSession] = React.useState(null);
  const [claims, setClaims] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(null); // Will be set based on protocol
  const [copied, setCopied] = React.useState(false);
  
  React.useEffect(() => {
    loadResults();
  }, [sessionId]);
  
  const loadResults = async () => {
    try {
      setLoading(true);
      
      // Load session
      const sessionResponse = await fetchWithUser(`/api/sessions/${sessionId}`);
      if (!sessionResponse.ok) throw new Error('Session not found');
      const sessionData = await sessionResponse.json();
      setSession(sessionData.session);
      
      // Set default tab based on protocol - SAML shows attributes, OIDC shows claims
      if (!activeTab) {
        setActiveTab(sessionData.session?.protocol === 'saml' ? 'attributes' : 'claims');
      }
      
      // Load claims
      const claimsResponse = await fetchWithUser(`/api/sessions/${sessionId}/claims`);
      if (claimsResponse.ok) {
        const claimsData = await claimsResponse.json();
        setClaims(claimsData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopy = (content) => {
    navigator.clipboard.writeText(JSON.stringify(content, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleDownload = (content, filename) => {
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card-heavy p-6 text-center bg-white">
          <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
          <h2 className="font-semibold mb-2 text-lg">Error</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => navigate('/configs')}
            className="btn btn-outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Configurations
          </button>
        </div>
      </div>
    );
  }
  
  const isSuccess = session?.status === 'completed';
  
  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/configs')}
        className="btn btn-ghost btn-sm mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </button>
      
      {/* Status Header */}
      <div className={`card-heavy p-6 mb-6 ${isSuccess ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
        <div className="flex items-center space-x-4">
          {isSuccess ? (
            <div className="p-2 rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          ) : (
            <div className="p-2 rounded-full bg-red-100">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold">
              {isSuccess ? 'Authentication Successful' : 'Authentication Failed'}
            </h1>
            <p className="text-sm text-muted-foreground">
              <span className="badge bg-primary/10 text-primary border-primary/20">
                {session?.protocol?.toUpperCase()}
              </span>
              {' '}Test • Session {sessionId?.slice(0, 8)}...
            </p>
          </div>
        </div>
      </div>
      
      {/* Session Info */}
      <div className="card p-4 mb-6 bg-white">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4 text-primary" />
              <span>{new Date(session?.createdAt).toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <User className="h-4 w-4 text-primary" />
              <span>{session?.protocol?.toUpperCase()}</span>
            </div>
          </div>
          <span className={`badge ${isSuccess ? 'bg-green-100 text-green-700 border-green-200' : 'badge-outline'}`}>
            {session?.status}
          </span>
        </div>
      </div>
      
      {/* Tabs - Show Claims for OIDC, Attributes for SAML */}
      <div className="border-b border-border mb-4">
        <div className="flex space-x-4">
          {[
            // Show Claims tab only for OIDC
            ...(session?.protocol === 'oidc' ? [{ id: 'claims', label: 'Claims' }] : []),
            // Show Attributes tab only for SAML
            ...(session?.protocol === 'saml' ? [{ id: 'attributes', label: 'Attributes' }] : []),
            { id: 'raw', label: 'Raw Response' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="card-heavy p-6 bg-white">
        {activeTab === 'claims' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-primary">Claims</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleCopy(claims?.claims || {})}
                  className="btn btn-outline btn-sm"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={() => handleDownload(claims?.claims || {}, `claims-${sessionId}.json`)}
                  className="btn btn-outline btn-sm"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </button>
              </div>
            </div>
            
            {claims?.claims ? (
              <ClaimsTable claims={claims.claims} />
            ) : (
              <p className="text-muted-foreground">No claims available</p>
            )}
          </div>
        )}
        
        {activeTab === 'attributes' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-primary">Attributes</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleCopy(claims?.attributes || {})}
                  className="btn btn-outline btn-sm"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </button>
                <button
                  onClick={() => handleDownload(claims?.attributes || {}, `attributes-${sessionId}.json`)}
                  className="btn btn-outline btn-sm"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </button>
              </div>
            </div>
            
            {claims?.attributes ? (
              <ClaimsTable claims={claims.attributes} />
            ) : (
              <p className="text-muted-foreground">No attributes available</p>
            )}
          </div>
        )}
        
        {activeTab === 'raw' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-primary">Raw Response</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleCopy(claims?.rawResponse || {})}
                  className="btn btn-outline btn-sm"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </button>
                <button
                  onClick={() => handleDownload(claims?.rawResponse || {}, `raw-${sessionId}.json`)}
                  className="btn btn-outline btn-sm"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </button>
              </div>
            </div>
            
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs border border-border">
              {JSON.stringify(claims?.rawResponse, null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex justify-between mt-6">
        <button
          onClick={() => navigate('/configs')}
          className="btn btn-outline"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Configurations
        </button>
        
        <button
          onClick={() => navigate(`/test/${session?.configId}`)}
          className="btn btn-gold"
        >
          <Play className="h-4 w-4 mr-2" />
          Run Another Test
        </button>
      </div>
    </div>
  );
}

function ClaimsTable({ claims }) {
  const entries = Object.entries(claims);
  
  if (entries.length === 0) {
    return <p className="text-muted-foreground">No claims available</p>;
  }
  
  return (
    <div className="overflow-x-auto border border-border rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="text-left py-3 px-4 font-medium text-primary">Claim</th>
            <th className="text-left py-3 px-4 font-medium text-primary">Value</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-border last:border-0 hover:bg-muted/30">
              <td className="py-3 px-4 pr-4 font-mono text-xs text-primary font-medium">{key}</td>
              <td className="py-3 px-4">
                {typeof value === 'object' ? (
                  <pre className="text-xs bg-muted p-2 rounded border border-border">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                ) : (
                  <span className="font-mono text-xs">{String(value)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
